import apiClient from '@/api/client';

// Types for authentication
export interface LoginRequest {
    username: string;
    password: string;
}

export interface LoginResponse {
    access_token: string;
    token_type: string;
    expires_in: number;
    refresh_token: string;
    user: UserInfo;
}

export interface UserInfo {
    id: number;
    username: string;
    email: string;
    full_name: string;
    role: 'admin' | 'operator' | 'viewer';
    is_active: boolean;
    last_login: string | null;
    created_at: string;
}

export interface PasswordChangeRequest {
    current_password: string;
    new_password: string;
}

class AuthService {
    /**
     * Authenticate user with username and password
     */
    async login(credentials: LoginRequest): Promise<LoginResponse> {
        const response = await apiClient.post<LoginResponse>('/api/auth/login', credentials);

        // Store tokens
        if (response.access_token) {
            localStorage.setItem('auth_token', response.access_token);
            localStorage.setItem('refresh_token', response.refresh_token);
            apiClient.setAuthToken(response.access_token);
        }

        return response;
    }

    /**
     * Logout the current user
     */
    async logout(): Promise<void> {
        try {
            await apiClient.post('/api/auth/logout');
        } catch (error) {
            // Even if logout fails on server, clear local tokens
            console.error('Logout error:', error);
        } finally {
            // Clear local storage
            localStorage.removeItem('auth_token');
            localStorage.removeItem('refresh_token');
            apiClient.clearAuthToken();

            // Redirect to login
            window.location.href = '/login';
        }
    }

    /**
     * Get current user information
     */
    async getCurrentUser(): Promise<UserInfo> {
        return await apiClient.get<UserInfo>('/api/auth/me');
    }

    /**
     * Refresh access token using refresh token
     */
    async refreshToken(): Promise<LoginResponse> {
        const refreshToken = localStorage.getItem('refresh_token');

        if (!refreshToken) {
            throw new Error('No refresh token available');
        }

        const response = await apiClient.post<LoginResponse>('/api/auth/refresh', {
            refresh_token: refreshToken,
        });

        // Update tokens
        if (response.access_token) {
            localStorage.setItem('auth_token', response.access_token);
            if (response.refresh_token) {
                localStorage.setItem('refresh_token', response.refresh_token);
            }
            apiClient.setAuthToken(response.access_token);
        }

        return response;
    }

    /**
     * Change user password
     */
    async changePassword(userId: number, passwordData: PasswordChangeRequest): Promise<void> {
        await apiClient.put(`/api/users/${userId}/password`, passwordData);
    }

    /**
     * Check if user is authenticated
     */
    isAuthenticated(): boolean {
        const token = localStorage.getItem('auth_token');
        const refreshToken = localStorage.getItem('refresh_token');

        if (!token) {
            // No session token; consider authenticated only if refresh token exists (can refresh on demand)
            return Boolean(refreshToken);
        }

        // Check if token is expired
        try {
            // Parse JWT payload (base64 decode)
            const payload = JSON.parse(atob(token.split('.')[1]));
            const expiry = payload.exp * 1000; // Convert to milliseconds

            if (Date.now() >= expiry) {
                // Session token expired. If refresh token exists, allow flow to proceed (client will refresh on 401)
                if (refreshToken) return true;
                // No refresh path available: clear and report unauthenticated
                this.clearTokens();
                return false;
            }

            return true;
        } catch (error) {
            console.error('Error parsing token:', error);
            // If parse fails but refresh token exists, allow auth flow to proceed so interceptor can refresh
            if (refreshToken) return true;
            // Otherwise clear
            this.clearTokens();
            return false;
        }
    }

    /**
     * Get current user from token
     */
    getUserFromToken(): UserInfo | null {
        const token = localStorage.getItem('auth_token');

        if (!token) {
            return null;
        }

        try {
            // Parse JWT payload
            const payload = JSON.parse(atob(token.split('.')[1]));

            const rawSub = payload?.sub;
            const id = typeof rawSub === 'number' ? rawSub : typeof rawSub === 'string' ? Number.parseInt(rawSub, 10) : 0;

            const username =
                typeof payload?.username === 'string'
                    ? payload.username
                    : typeof payload?.preferred_username === 'string'
                        ? payload.preferred_username
                        : typeof rawSub === 'string'
                            ? rawSub
                            : `user_${id || '0'}`;

            const email = typeof payload?.email === 'string' ? payload.email : '';
            const full_name =
                typeof payload?.full_name === 'string'
                    ? payload.full_name
                    : typeof payload?.name === 'string'
                        ? payload.name
                        : '';

            const rawRole = typeof payload?.role === 'string' ? payload.role : '';
            const allowedRoles = ['admin', 'operator', 'viewer'] as const;
            const role = (allowedRoles as readonly string[]).includes(rawRole) ? (rawRole as UserInfo['role']) : 'viewer';

            const created_at = typeof payload?.created_at === 'string' ? payload.created_at : '';
            const last_login = typeof payload?.last_login === 'string' ? payload.last_login : null;

            return {
                id,
                username,
                email,
                full_name,
                role,
                is_active: true,
                last_login,
                created_at,
            };
        } catch (error) {
            console.error('Error parsing token:', error);
            return null;
        }
    }

    /**
     * Clear all authentication tokens
     */
    private clearTokens(): void {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('refresh_token');
        apiClient.clearAuthToken();
    }
}

// Export singleton instance
export const authService = new AuthService();
